import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavigation } from '@platform/entitlements';
import type { PortalModule } from '../../api';
import { ModulePlaceholder } from '../ModulePlaceholder';
import { SectionIndex } from '../SectionIndex';
import { PlatformAnalysis } from './strategic-intelligence/PlatformAnalysis';
import { AiPlatformFit } from './strategic-intelligence/AiPlatformFit';
import { PlatformFitSummary } from './strategic-intelligence/PlatformFitSummary';
import { AiPlatformsCatalog } from './strategic-intelligence/AiPlatformsCatalog';
import { AiPlatformDetail } from './strategic-intelligence/AiPlatformDetail';
import { PlatformFitmentCriteria } from './strategic-intelligence/PlatformFitmentCriteria';
import { PlatformFitmentCriteriaWorkspace } from './strategic-intelligence/PlatformFitmentCriteria.workspace';
import { AiPlatformsCatalogWorkspace } from './strategic-intelligence/AiPlatformsCatalog.workspace';
import { AiPlatformDetailWorkspace } from './strategic-intelligence/AiPlatformDetail.workspace';
import { PlatformAnalysisWorkspace } from './strategic-intelligence/PlatformAnalysis.workspace';
import { AiPlatformFitWorkspace } from './strategic-intelligence/AiPlatformFit.workspace';
import { PlatformFitSummaryWorkspace } from './strategic-intelligence/PlatformFitSummary.workspace';
import { UserGuideWorkspace } from './help/UserGuide.workspace';
import { HelpSupportWorkspace } from './help/HelpSupport.workspace';
import { isWorkspaceUi } from '../shared/workspaceUi';
import { AiReadinessStartPage } from './StartPage';
import { AiReadinessStartPageWorkspace } from './StartPage.workspace';
import { UserGuide } from './help/UserGuide';
import { HelpSupport } from './help/HelpSupport';

/**
 * AI Readiness — screen resolution, the same shape AprModule uses.
 *
 * Keyed by the section-key trail, so a route is derived from the catalogue rather than
 * written twice. A key with no entry here falls through to ModulePlaceholder, which is
 * how the not-yet-ported screens in the tree announce themselves instead of erroring.
 *
 * The tree itself is the SOURCE'S, cloned from `layout-v2.ejs`: Strategic Intelligence
 * with its six screens, and a Settings bucket carrying only AI Platform Selection.
 */
const SCREENS: Record<string, () => JSX.Element> = {
  /* The two bottom-group leaves, keyed by their catalogue slugs. */
  'user-guide': () => <UserGuide />,
  'help-support': () => <HelpSupport />,
  'strategic-intelligence/platform-analysis': () => <PlatformAnalysis />,
  'strategic-intelligence/ai-platform-fitness-analysis': () => <AiPlatformFit />,
  'strategic-intelligence/platform-fit-summary': () => <PlatformFitSummary />,
  'strategic-intelligence/ai-platforms-catalog': () => <AiPlatformsCatalog />,
  /*
   * MOVED to Settings → AI Platform Selection, owner-directed 2026-08-17, and RENAMED with
   * the menu on the same instruction.
   *
   * THE TWO OLD PATHS ARE DELETED, NOT REDIRECTED — "I don't want any reference to any old
   * URL." An earlier pass kept them as `<Navigate>` entries to spare anyone's bookmark;
   * the owner's ruling is that no trace of the old addresses remains, so they are gone and
   * an old link now falls through to the module's not-found handling like any other bad
   * path.
   */
  'settings/ai-platform-selection/platform-fitment-criteria': () => <PlatformFitmentCriteria />,
};

/**
 * Decision Workspace versions of screens, shown INSTEAD of the ported original when the address
 * carries `?ui=workspace` (pages/shared/workspaceUi.ts). The original stays the default until the
 * owner approves each one; approving a screen deletes its original and its entry here.
 */
const WORKSPACE_SCREENS: Record<string, () => JSX.Element> = {
  'user-guide': () => <UserGuideWorkspace />,
  'help-support': () => <HelpSupportWorkspace />,
  'strategic-intelligence/platform-fit-summary': () => <PlatformFitSummaryWorkspace />,
  'strategic-intelligence/ai-platform-fitness-analysis': () => <AiPlatformFitWorkspace />,
  'strategic-intelligence/platform-analysis': () => <PlatformAnalysisWorkspace />,
  'strategic-intelligence/ai-platforms-catalog': () => <AiPlatformsCatalogWorkspace />,
  'settings/ai-platform-selection/platform-fitment-criteria': () => <PlatformFitmentCriteriaWorkspace />,
};

export interface AiReadinessModuleProps {
  modules: PortalModule[];
  /** Greets the user, as the source's start screen does. */
  firstName?: string;
}

export function AiReadinessModule({ modules, firstName }: AiReadinessModuleProps) {
  // `modules` holds only what this user may open, sections already filtered. Anything
  // else is a typed URL and is redirected rather than rendering a screen whose API calls
  // the server would refuse.
  const module = modules.find((m) => m.id === 'ai_readiness');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;

  return <AiReadinessContent modules={modules} module={module} firstName={firstName} />;
}

/**
 * A record id below a screen — `.../ai-platforms-catalog/17`.
 *
 * `resolveNavigation` walks catalogue SECTIONS, and a platform id is not one, so the
 * trail stops at the catalogue and the extra segment has to be read off the path. Same
 * pattern the catalogue uses for depth: the route is derived, the id is not invented.
 */
function recordIdBelow(pathname: string, sectionPath: string): string | null {
  if (!pathname.startsWith(`${sectionPath}/`)) return null;
  const rest = pathname.slice(sectionPath.length + 1).split('/').filter(Boolean);
  return rest.length === 1 ? rest[0]! : null;
}

function AiReadinessContent({
  modules,
  module,
  firstName,
}: {
  modules: PortalModule[];
  module: PortalModule;
  firstName?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const at = resolveNavigation(location.pathname, [module]);

  // `/ai_readiness` is the product's own Start Menu, cloned from the source's
  // start-menu.ejs — the screen it shows when you enter the product (owner's ruling,
  // 2026-08-04). Not a list of buckets: those stay reachable from the left nav.
  if (at.trail.length === 0) {
    return isWorkspaceUi(location.search) ? (
      <AiReadinessStartPageWorkspace firstName={firstName} sections={module.sections} />
    ) : (
      <AiReadinessStartPage firstName={firstName} sections={module.sections} />
    );
  }

  const deepest = at.trail[at.trail.length - 1]!;

  if (!at.section) {
    return (
      <SectionIndex
        title={deepest.label}
        sections={deepest.sections ?? []}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const key = at.trail.map((s) => s.key).join('/');

  // One platform, opened from the catalogue.
  if (key === 'strategic-intelligence/ai-platforms-catalog') {
    const id = recordIdBelow(location.pathname, at.section.path);
    if (id)
      return isWorkspaceUi(location.search) ? (
        <AiPlatformDetailWorkspace platformId={id} />
      ) : (
        <AiPlatformDetail platformId={id} />
      );
  }

  const render = (isWorkspaceUi(location.search) ? WORKSPACE_SCREENS[key] : undefined) ?? SCREENS[key];
  if (!render) return (
      <ModulePlaceholder
        modules={modules}
        module={module}
        section={at.section ? { key: at.section.key, label: at.section.label } : undefined}
      />
    );
  return render();
}
